<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Lister Items</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage product categories and their hierarchy</p>
                                </div>
                                <div class="flex items-center gap-3 flex-wrap">
                                        <!-- Refresh Button -->
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors hover:bg-gray-50"
                                                @click="refreshData"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6"
                                                @click="openCreateModal(null)"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create New
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <div class="lister-main-layout flex gap-3 items-start">
                                <!-- File Tree Sidebar -->
                                <div class="lister-sidebar w-[280px] min-w-[280px] bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-sm font-semibold text-gray-900 m-0">Categories</h2>
                                        </div>
                                        <div class="p-2">
                                                <!-- Search -->
                                                <div class="relative mb-2">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                        >
                                                </div>
                                                <!-- Loading State -->
                                                <div v-if="isLoading" class="flex justify-center items-center py-8 px-4">
                                                        <svg class="w-6 h-6 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                </div>
                                                <!-- Tree -->
                                                <div v-else class="tree-container max-h-[calc(100vh-280px)] overflow-y-auto">
                                                        <div v-if="filteredTreeData.length === 0" class="p-4 text-center text-gray-500 text-sm">
                                                                No categories found
                                                        </div>
                                                        <div v-for="item in filteredTreeData" :key="item.id">
                                                                <TreeNode
                                                                        :item="item"
                                                                        :level="0"
                                                                        :expanded-ids="expandedIds"
                                                                        :selected-id="selectedId"
                                                                        :drag-over-id="dragOverId"
                                                                        :drop-position="dropPosition"
                                                                        @toggle="toggleExpand"
                                                                        @select="selectItem"
                                                                        @drag-start="onTreeDragStart"
                                                                        @drag-over="onTreeDragOver"
                                                                        @drag-leave="onTreeDragLeave"
                                                                        @drop="onTreeDrop"
                                                                        @drag-end="onTreeDragEnd"
                                                                />
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Cards Grid -->
                                <div class="flex-1 min-w-0">
                                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                                <div class="py-4 px-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                                                        <div class="flex items-center gap-3">
                                                                <h2 class="text-base font-semibold text-gray-900 m-0">
                                                                        {{ selectedItem ? selectedItem.name : 'All Categories' }}
                                                                </h2>
                                                                <span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
                                                                        {{ displayedItems.length }} items
                                                                </span>
                                                        </div>
                                                        <button
                                                                v-if="selectedItem"
                                                                type="button"
                                                                class="inline-flex items-center gap-1 py-1.5 px-3 bg-orange-7 text-white border-none rounded-md text-xs font-medium cursor-pointer hover:bg-orange-6 transition-colors"
                                                                @click="openCreateModal(selectedItem.id)"
                                                        >
                                                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                </svg>
                                                                Add Child
                                                        </button>
                                                </div>
                                                <div class="p-4">
                                                        <!-- Loading State -->
                                                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                        </div>
                                                        <!-- Empty State -->
                                                        <div v-else-if="displayedItems.length === 0" class="text-center py-12 px-4">
                                                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                                                </svg>
                                                                <h3 class="text-base font-medium text-gray-700 m-0 mb-1">No items</h3>
                                                                <p class="text-sm text-gray-500 m-0">
                                                                        {{ selectedItem ? 'This category has no children. Add a child category to get started.' : 'Create your first category to get started.' }}
                                                                </p>
                                                        </div>
                                                        <!-- Cards Grid -->
                                                        <div v-else class="flex flex-col gap-4">
                                                                <div
                                                                        v-for="item in displayedItems"
                                                                        :key="item.id"
                                                                        class="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all hover:shadow-md hover:border-gray-300"
                                                                >
                                                                        <!-- Card Image -->
                                                                        <div class="relative h-[140px] bg-gray-100">
                                                                                <img
                                                                                        v-if="getDisplayImage(item)"
                                                                                        :src="getDisplayImage(item)"
                                                                                        :alt="item.name"
                                                                                        class="w-full h-full object-contain"
                                                                                >
                                                                                <div v-else class="w-full h-full flex items-center justify-center">
                                                                                        <svg class="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                        </svg>
                                                                                </div>
                                                                                <!-- Upload Button -->
                                                                                <button
                                                                                        v-if="hasFilestackClient"
                                                                                        class="absolute bottom-2 right-2 p-1.5 bg-white/90 border-none rounded-md cursor-pointer flex items-center justify-center hover:bg-white transition-colors"
                                                                                        title="Upload Image"
                                                                                        @click.stop="openImagePicker(item)"
                                                                                >
                                                                                        <svg class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <!-- Children Badge -->
                                                                                <div
                                                                                        v-if="item.children && item.children.length > 0"
                                                                                        class="absolute top-2 left-2 py-1 px-2 bg-orange-7/90 text-white rounded text-[10px] font-semibold"
                                                                                >
                                                                                        {{ item.children.length }} {{ item.children.length === 1 ? 'child' : 'children' }}
                                                                                </div>
                                                                        </div>
                                                                        <!-- Card Content -->
                                                                        <div class="p-4">
                                                                                <!-- Properties Section -->
                                                                                <div class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                                        <div class="text-xs font-semibold text-orange-7 mb-3 flex items-center gap-1">
                                                                                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                                                </svg>
                                                                                                Properties
                                                                                                <span class="ml-auto text-[11px] text-gray-400 font-normal">#{{ item.id }}</span>
                                                                                        </div>
                                                                                        <!-- Name -->
                                                                                        <div class="mb-3">
                                                                                                <label class="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Name</label>
                                                                                                <input
                                                                                                        v-model="item.name"
                                                                                                        type="text"
                                                                                                        placeholder="Enter name..."
                                                                                                        class="w-full py-1.5 px-2 border border-gray-200 rounded-md text-[13px] outline-none box-border bg-white focus:border-orange-7"
                                                                                                >
                                                                                        </div>
                                                                                        <!-- PDP -->
                                                                                        <div class="mb-3">
                                                                                                <label class="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">PDP</label>
                                                                                                <select
                                                                                                        v-model="item.pdpid"
                                                                                                        class="w-full py-1.5 px-2 border border-gray-200 rounded-md text-[13px] outline-none box-border bg-white focus:border-orange-7"
                                                                                                >
                                                                                                        <option :value="null">None</option>
                                                                                                        <option v-for="pdp in pdps" :key="pdp.id" :value="pdp.id">
                                                                                                                {{ pdp.id }} | {{ pdp.name }}
                                                                                                        </option>
                                                                                                </select>
                                                                                        </div>
                                                                                </div>
                                                                                <!-- Translations with language tabs -->
                                                                                <template v-if="getItemLanguages(item).length > 0">
                                                                                <div class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                                        <!-- Language tabs -->
                                                                                        <div class="flex items-center gap-1 mb-3 border-b border-gray-200 pb-2">
                                                                                                <svg class="w-3.5 h-3.5 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                                                                </svg>
                                                                                                <button
                                                                                                        v-for="lang in getItemLanguages(item)"
                                                                                                        :key="lang.id"
                                                                                                        type="button"
                                                                                                        class="py-1 px-2.5 text-xs font-medium rounded-md transition-colors border-none cursor-pointer"
                                                                                                        :class="getActiveLanguage(item) === lang.id
                                                                                                                ? 'bg-orange-7 text-white'
                                                                                                                : 'bg-white text-gray-600 hover:bg-gray-100'"
                                                                                                        @click="selectedCardLanguage[item.id] = lang.id"
                                                                                                >
                                                                                                        {{ lang.name }}
                                                                                                </button>
                                                                                        </div>
                                                                                        <!-- Active language content -->
                                                                                        <template v-for="lang in getItemLanguages(item)" :key="'content-' + lang.id">
                                                                                                <div v-if="getActiveLanguage(item) === lang.id">
                                                                                                        <div class="mb-2">
                                                                                                                <label class="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Header</label>
                                                                                                                <input
                                                                                                                        v-model="item.translations[lang.id].header"
                                                                                                                        type="text"
                                                                                                                        placeholder="Enter header..."
                                                                                                                        class="w-full py-1.5 px-2 border border-gray-200 rounded-md text-[13px] outline-none box-border bg-white focus:border-orange-7"
                                                                                                                >
                                                                                                        </div>
                                                                                                        <div>
                                                                                                                <label class="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Description</label>
                                                                                                                <textarea
                                                                                                                        v-model="item.translations[lang.id].description"
                                                                                                                        placeholder="Enter description..."
                                                                                                                        rows="2"
                                                                                                                        class="w-full py-1.5 px-2 border border-gray-200 rounded-md text-[13px] outline-none resize-y box-border bg-white focus:border-orange-7"
                                                                                                                ></textarea>
                                                                                                        </div>
                                                                                                </div>
                                                                                        </template>
                                                                                </div>
                                                                                </template>
                                                                                <!-- Actions -->
                                                                                <div class="flex gap-2 justify-end">
                                                                                        <button
                                                                                                v-if="hasItemChanges(item.id)"
                                                                                                class="inline-flex items-center gap-1 py-1.5 px-3 bg-gray-100 text-gray-500 border border-gray-300 rounded-md text-xs font-medium cursor-pointer transition-colors hover:bg-gray-200"
                                                                                                @click="cancelChanges(item)"
                                                                                        >
                                                                                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                                                </svg>
                                                                                                Cancel
                                                                                        </button>
                                                                                        <button
                                                                                                :disabled="!hasItemChanges(item.id)"
                                                                                                :class="[
                                                                                                        'inline-flex items-center gap-1 py-1.5 px-3 border-none rounded-md text-xs font-medium transition-colors',
                                                                                                        hasItemChanges(item.id)
                                                                                                                ? 'bg-orange-7 text-white cursor-pointer hover:bg-orange-6'
                                                                                                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                                                                ]"
                                                                                                @click="saveItem(item)"
                                                                                        >
                                                                                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                                                                </svg>
                                                                                                Save
                                                                                        </button>
                                                                                        
                                                                                        <button
                                                                                                class="inline-flex items-center gap-1 py-1.5 px-3 bg-red-50 text-red-600 border-none rounded-md text-xs font-medium cursor-pointer transition-colors hover:bg-red-100"
                                                                                                @click="confirmDelete(item)"
                                                                                        >
                                                                                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                                </svg>
                                                                                                Delete
                                                                                        </button>
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm m-4">
                                <div class="flex items-center justify-between py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">
                                                {{ createParentId ? 'Create Child Category' : 'Create Category' }}
                                        </h3>
                                        <button
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1 hover:text-gray-600"
                                                @click="closeCreateModal"
                                        >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                        <input
                                                v-model="newItemName"
                                                type="text"
                                                placeholder="Enter category name..."
                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                @keyup.enter="createItem"
                                        >
                                        <p v-if="createParentId" class="mt-3 text-[13px] text-gray-500">
                                                This will be created as a child of "{{ selectedItem?.name }}"
                                        </p>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                class="py-2 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                                                @click="closeCreateModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                :disabled="isCreating || !newItemName.trim()"
                                                class="py-2.5 px-5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-60 disabled:cursor-not-allowed"
                                                @click="createItem"
                                        >
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                :class="[
                                        'flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg',
                                        toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'
                                ]"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer p-0 hover:opacity-100"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance, onMounted, h } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import Swal from 'sweetalert2';
import ProductCategoryService from 'services/ProductCategoryService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';
import PDPService from 'services/PDPService';
import { httpClient } from 'utils/http';

const TreeNode = defineComponent({
        name: 'TreeNode',
        props: {
                item: { type: Object, required: true },
                level: { type: Number, default: 0 },
                expandedIds: { type: Object, required: true },
                selectedId: { type: Number, default: null },
                dragOverId: { type: Number, default: null },
                dropPosition: { type: String, default: null },
        },
        emits: ['toggle', 'select', 'drag-start', 'drag-over', 'drag-leave', 'drop', 'drag-end'],
        setup(props, { emit }) {
                const hasChildren = computed(() => props.item.children && props.item.children.length > 0);
                const isExpanded = computed(() => props.expandedIds[props.item.id]);
                const isSelected = computed(() => props.selectedId === props.item.id);
                const isDragOver = computed(() => props.dragOverId === props.item.id);

                const toggle = (e) => {
                        e.stopPropagation();
                        emit('toggle', props.item.id);
                };

                const select = () => {
                        emit('select', props.item);
                };

                const handleDragStart = (e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', props.item.id.toString());
                        emit('drag-start', props.item);
                };

                const handleDragOver = (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        const rect = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const height = rect.height;
                        let position = 'inside';
                        if (y < height * 0.25) {
                                position = 'before';
                        } else if (y > height * 0.75) {
                                position = 'after';
                        }
                        emit('drag-over', props.item, position);
                };

                const handleDragLeave = (e) => {
                        emit('drag-leave', e);
                };

                const handleDrop = (e) => {
                        e.preventDefault();
                        emit('drop', props.item);
                };

                const handleDragEnd = () => {
                        emit('drag-end');
                };

                return { hasChildren, isExpanded, isSelected, isDragOver, toggle, select, handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd };
        },
        render() {
                const paddingLeft = `${this.level * 1}rem`;
                const isDragOverBefore = this.isDragOver && this.dropPosition === 'before';
                const isDragOverAfter = this.isDragOver && this.dropPosition === 'after';
                const isDragOverInside = this.isDragOver && this.dropPosition === 'inside';

                const wrapperStyle = {
                        position: 'relative',
                };

                const beforeIndicatorStyle = {
                        position: 'absolute',
                        top: '0',
                        left: `calc(0.75rem + ${paddingLeft})`,
                        right: '0.5rem',
                        height: '2px',
                        backgroundColor: '#9333ea',
                        opacity: isDragOverBefore ? '1' : '0',
                        transition: 'opacity 0.15s',
                };

                const afterIndicatorStyle = {
                        position: 'absolute',
                        bottom: '0',
                        left: `calc(0.75rem + ${paddingLeft})`,
                        right: '0.5rem',
                        height: '2px',
                        backgroundColor: '#9333ea',
                        opacity: isDragOverAfter ? '1' : '0',
                        transition: 'opacity 0.15s',
                };

                const nodeStyle = {
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.5rem 0.75rem',
                        paddingLeft: `calc(0.75rem + ${paddingLeft})`,
                        cursor: 'grab',
                        borderRadius: '0.375rem',
                        marginBottom: '0.125rem',
                        backgroundColor: isDragOverInside ? '#f3e8ff' : (this.isSelected ? '#f3e8ff' : 'transparent'),
                        border: isDragOverInside ? '2px dashed #9333ea' : '2px solid transparent',
                        transition: 'background-color 0.15s, border-color 0.15s',
                };

                const chevronStyle = {
                        width: '1rem',
                        height: '1rem',
                        marginRight: '0.375rem',
                        color: '#9ca3af',
                        flexShrink: 0,
                        transform: this.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                };

                const folderStyle = {
                        width: '1rem',
                        height: '1rem',
                        marginRight: '0.5rem',
                        color: this.hasChildren ? '#9333ea' : '#6b7280',
                        flexShrink: 0,
                };

                const nameStyle = {
                        fontSize: '0.875rem',
                        color: this.isSelected ? '#9333ea' : '#374151',
                        fontWeight: this.isSelected ? '500' : '400',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                };

                const dragHandleStyle = {
                        width: '1rem',
                        height: '1rem',
                        color: '#9ca3af',
                        marginRight: '0.25rem',
                        flexShrink: 0,
                        cursor: 'grab',
                };

                const children = [];

                children.push(
                        h('svg', {
                                style: dragHandleStyle,
                                fill: 'none',
                                viewBox: '0 0 24 24',
                                stroke: 'currentColor',
                                'stroke-width': '2',
                        }, [
                                h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M4 8h16M4 16h16' })
                        ])
                );

                if (this.hasChildren) {
                        children.push(
                                h('svg', {
                                        style: chevronStyle,
                                        fill: 'none',
                                        viewBox: '0 0 24 24',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        onClick: this.toggle,
                                }, [
                                        h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M9 5l7 7-7 7' })
                                ])
                        );
                } else {
                        children.push(h('div', { style: { width: '1rem', marginRight: '0.375rem' } }));
                }

                children.push(
                        h('svg', {
                                style: folderStyle,
                                fill: 'none',
                                viewBox: '0 0 24 24',
                                stroke: 'currentColor',
                                'stroke-width': '1.5',
                        }, [
                                h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: this.hasChildren ? 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' : 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' })
                        ])
                );

                children.push(h('span', { style: nameStyle }, this.item.name));

                const nodeElement = h('div', {
                        style: nodeStyle,
                        draggable: true,
                        onClick: this.select,
                        onDragstart: this.handleDragStart,
                        onDragover: this.handleDragOver,
                        onDragleave: this.handleDragLeave,
                        onDrop: this.handleDrop,
                        onDragend: this.handleDragEnd,
                        onMouseenter: (e) => { if (!this.isSelected && !this.isDragOver) e.currentTarget.style.backgroundColor = '#f9fafb'; },
                        onMouseleave: (e) => { if (!this.isSelected && !this.isDragOver) e.currentTarget.style.backgroundColor = 'transparent'; },
                }, children);

                const result = [
                        h('div', { style: wrapperStyle }, [
                                h('div', { style: beforeIndicatorStyle }),
                                nodeElement,
                                h('div', { style: afterIndicatorStyle }),
                        ])
                ];

                if (this.hasChildren && this.isExpanded) {
                        for (const child of this.item.children) {
                                result.push(
                                        h(TreeNode, {
                                                item: child,
                                                level: this.level + 1,
                                                expandedIds: this.expandedIds,
                                                selectedId: this.selectedId,
                                                dragOverId: this.dragOverId,
                                                dropPosition: this.dropPosition,
                                                onToggle: (id) => this.$emit('toggle', id),
                                                onSelect: (item) => this.$emit('select', item),
                                                onDragStart: (item) => this.$emit('drag-start', item),
                                                onDragOver: (item, pos) => this.$emit('drag-over', item, pos),
                                                onDragLeave: (e) => this.$emit('drag-leave', e),
                                                onDrop: (item) => this.$emit('drop', item),
                                                onDragEnd: () => this.$emit('drag-end'),
                                        })
                                );
                        }
                }

                return h('div', result);
        },
});

export default defineComponent({
        name: 'ListerItemsPage',
        components: { TreeNode },
        setup() {
                const auth0 = useAuth0();
                const instance = getCurrentInstance();
                const filestackClient = instance?.appContext.config.globalProperties.$client;

                const allItems = ref([]);
                const languages = ref([]);
                const selectedLanguage = ref('');
                const translations = ref({});
                const isLoading = ref(true);
                const searchQuery = ref('');
                const pdps = ref([]);

                const expandedIds = ref({});
                const selectedId = ref(null);

                const showCreateModal = ref(false);
                const newItemName = ref('');
                const createParentId = ref(null);
                const isCreating = ref(false);

                const toastMessage = ref('');
                const toastVariant = ref('success');
                let toastTimeout;

                const draggedItem = ref(null);
                const dragOverId = ref(null);
                const dropPosition = ref(null);

                const originalItemValues = ref({});
                const pendingImages = ref({});
                const selectedCardLanguage = ref({});

                const getItemLanguages = (item) => {
                        if (!item.translations) return [];
                        return languages.value.filter(l => item.translations[l.id]);
                };

                const getActiveLanguage = (item) => {
                        const itemLangs = getItemLanguages(item);
                        if (itemLangs.length === 0) return null;
                        return selectedCardLanguage.value[item.id] || itemLangs[0].id;
                };

                const loggedIn = computed(() => auth0.isAuthenticated.value);
                const hasFilestackClient = computed(() => !!filestackClient);

                const buildTree = (items) => {
                        const itemMap = {};
                        const roots = [];

                        items.forEach(item => {
                                itemMap[item.id] = { ...item, children: [] };
                        });

                        items.forEach(item => {
                                if (item.parentid && itemMap[item.parentid]) {
                                        itemMap[item.parentid].children.push(itemMap[item.id]);
                                } else if (!item.parentid) {
                                        roots.push(itemMap[item.id]);
                                }
                        });

                        const sortBySerialNumber = (nodes) => {
                                nodes.sort((a, b) => (a.serialnumber ?? 0) - (b.serialnumber ?? 0));
                                nodes.forEach(node => {
                                        if (node.children && node.children.length > 0) {
                                                sortBySerialNumber(node.children);
                                        }
                                });
                        };

                        sortBySerialNumber(roots);
                        return roots;
                };

                const treeData = computed(() => buildTree(allItems.value));

                const filteredTreeData = computed(() => {
                        if (!searchQuery.value) return treeData.value;
                        const query = searchQuery.value.toLowerCase();

                        const filterTree = (nodes) => {
                                return nodes.reduce((acc, node) => {
                                        const matchesSearch = node.name?.toLowerCase().includes(query) ||
                                                node.header?.toLowerCase().includes(query);
                                        const filteredChildren = filterTree(node.children || []);

                                        if (matchesSearch || filteredChildren.length > 0) {
                                                acc.push({
                                                        ...node,
                                                        children: filteredChildren,
                                                });
                                        }
                                        return acc;
                                }, []);
                        };

                        return filterTree(treeData.value);
                });

                const selectedItem = computed(() => {
                        if (!selectedId.value) return null;
                        const findItem = (nodes) => {
                                for (const node of nodes) {
                                        if (node.id === selectedId.value) return node;
                                        if (node.children) {
                                                const found = findItem(node.children);
                                                if (found) return found;
                                        }
                                }
                                return null;
                        };
                        return findItem(treeData.value);
                });

                const displayedItems = computed(() => {
                        if (!selectedId.value) {
                                return treeData.value;
                        }
                        return selectedItem.value ? [selectedItem.value] : [];
                });

                const findItemInTree = (nodes, itemId) => {
                        for (const node of nodes) {
                                if (node.id === itemId) return node;
                                if (node.children && node.children.length > 0) {
                                        const found = findItemInTree(node.children, itemId);
                                        if (found) return found;
                                }
                        }
                        return null;
                };

                const hasItemChanges = (itemId) => {
                        const original = originalItemValues.value[itemId];
                        if (!original) return false;
                        const item = findItemInTree(treeData.value, itemId);
                        if (!item) return false;
                        
                        if (pendingImages.value[itemId]) return true;
                        
                        if ((item.name || '') !== original.name) return true;
                        if ((item.pdpid || null) !== original.pdpid) return true;
                        
                        if (item.translations) {
                                for (const lang of languages.value) {
                                        const origTrans = original[lang.id];
                                        const currTrans = item.translations[lang.id];
                                        if (!origTrans) continue;
                                        if (currTrans?.header !== origTrans.header || currTrans?.description !== origTrans.description) {
                                                return true;
                                        }
                                }
                        }
                        return false;
                };
                
                const getDisplayImage = (item) => {
                        return pendingImages.value[item.id] || item.thumbnail;
                };

                const storeOriginalValues = (item) => {
                        if (item && !originalItemValues.value[item.id]) {
                                originalItemValues.value[item.id] = {
                                        name: item.name || '',
                                        pdpid: item.pdpid || null,
                                };
                                languages.value.forEach(lang => {
                                        originalItemValues.value[item.id][lang.id] = {
                                                header: item.translations?.[lang.id]?.header || '',
                                                description: item.translations?.[lang.id]?.description || '',
                                        };
                                });
                        }
                };

                const handleSaveButtonHover = (event, itemId, isEntering) => {
                        if (hasItemChanges(itemId)) {
                                event.currentTarget.style.backgroundColor = isEntering ? '#7e22ce' : '#9333ea';
                        }
                };

                const showToast = (message, variant = 'success') => {
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = () => {
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastMessage.value = '';
                };

                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const [langResponse, data, pdpsData] = await Promise.all([
                                        LanguageService.getAll(new URLSearchParams({ limit: '0' })),
                                        ProductCategoryService.getAll(new URLSearchParams({ limit: '0' })),
                                        PDPService.getAll(new URLSearchParams({ limit: '0' })),
                                ]);
                                languages.value = langResponse;
                                const defaultLang = langResponse.find(l => l.default);
                                selectedLanguage.value = defaultLang ? defaultLang.id : langResponse[0]?.id || '';
                                allItems.value = data;
                                pdps.value = pdpsData;

                                await loadTranslations();

                                if (!selectedId.value && treeData.value.length > 0) {
                                        selectedId.value = treeData.value[0].id;
                                }
                        } catch (error) {
                                showToast(error.message || 'Failed to load data', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadTranslations = async () => {
                        if (languages.value.length === 0) return;
                        try {
                                const langIds = languages.value.map(l => l.id).join(' ');
                                const params = new URLSearchParams({
                                        ns: 'productcategories',
                                        lng: langIds,
                                        limit: '0',
                                });
                                const data = await TranslationService.getMultiTranslation(params);
                                translations.value = {};
                                languages.value.forEach(lang => {
                                        translations.value[lang.id] = data[lang.id]?.productcategories || {};
                                });

                                allItems.value = allItems.value.map(item => {
                                        const itemTranslations = {};
                                        languages.value.forEach(lang => {
                                                const trans = translations.value[lang.id]?.[item.id];
                                                itemTranslations[lang.id] = {
                                                        header: trans?.name || '',
                                                        description: trans?.lines?.join('\n') || '',
                                                };
                                        });
                                        return {
                                                ...item,
                                                translations: itemTranslations,
                                        };
                                });
                                
                                // Update original values with loaded translations to prevent false change detection
                                // Only update if original translations were empty (not yet loaded) to avoid wiping pending edits
                                allItems.value.forEach(item => {
                                        if (originalItemValues.value[item.id]) {
                                                languages.value.forEach(lang => {
                                                        const origTrans = originalItemValues.value[item.id][lang.id];
                                                        // Only update if original translation was empty/unset
                                                        if (!origTrans || (origTrans.header === '' && origTrans.description === '')) {
                                                                originalItemValues.value[item.id][lang.id] = {
                                                                        header: item.translations?.[lang.id]?.header || '',
                                                                        description: item.translations?.[lang.id]?.description || '',
                                                                };
                                                        }
                                                });
                                        }
                                });
                        } catch (error) {
                                showToast('Failed to load translations', 'error');
                        }
                };

                const saveItem = async (item) => {
                        try {
                                const original = originalItemValues.value[item.id];
                                const pendingImage = pendingImages.value[item.id];
                                const hasPropertyChanges = original && (
                                        (item.name || '') !== original.name ||
                                        (item.pdpid || null) !== original.pdpid ||
                                        pendingImage
                                );
                                
                                if (hasPropertyChanges) {
                                        const updateData = {
                                                name: item.name,
                                                pdpid: item.pdpid,
                                        };
                                        if (pendingImage) {
                                                updateData.thumbnail = pendingImage;
                                        }
                                        await ProductCategoryService.update(item.id, updateData);
                                        
                                        if (pendingImage) {
                                                item.thumbnail = pendingImage;
                                                delete pendingImages.value[item.id];
                                        }
                                }
                                
                                const savePromises = languages.value.map(async (lang) => {
                                        const trans = item.translations?.[lang.id];
                                        if (!trans) return;
                                        const origTrans = originalItemValues.value[item.id]?.[lang.id];
                                        const hasTransChanges = !origTrans || trans.header !== origTrans.header || trans.description !== origTrans.description;
                                        if (!hasTransChanges) return;
                                        
                                        await httpClient.put('/api/translation', {
                                                namespace: 'productcategories',
                                                language: lang.id,
                                                bundle: {
                                                        [item.id]: {
                                                                name: trans.header || '',
                                                                lines: trans.description ? trans.description.split('\n') : null,
                                                        },
                                                },
                                        });
                                });
                                await Promise.all(savePromises);
                                
                                if (!originalItemValues.value[item.id]) {
                                        originalItemValues.value[item.id] = {};
                                }
                                originalItemValues.value[item.id].name = item.name || '';
                                originalItemValues.value[item.id].pdpid = item.pdpid || null;
                                languages.value.forEach(lang => {
                                        originalItemValues.value[item.id][lang.id] = {
                                                header: item.translations?.[lang.id]?.header || '',
                                                description: item.translations?.[lang.id]?.description || '',
                                        };
                                });
                                showToast('Changes saved');
                        } catch (error) {
                                showToast(error.message || 'Failed to save changes', 'error');
                        }
                };
                
                const cancelChanges = (item) => {
                        const original = originalItemValues.value[item.id];
                        if (!original) return;
                        
                        item.name = original.name;
                        item.pdpid = original.pdpid;
                        
                        languages.value.forEach(lang => {
                                if (item.translations?.[lang.id] && original[lang.id]) {
                                        item.translations[lang.id].header = original[lang.id].header;
                                        item.translations[lang.id].description = original[lang.id].description;
                                }
                        });
                        
                        delete pendingImages.value[item.id];
                        
                        showToast('Changes cancelled');
                };

                const refreshData = () => {
                        loadData();
                };

                const toggleExpand = (id) => {
                        expandedIds.value = { ...expandedIds.value, [id]: !expandedIds.value[id] };
                };

                const getItemsWithUnsavedChanges = () => {
                        const changedItems = [];
                        for (const item of displayedItems.value) {
                                if (hasItemChanges(item.id)) {
                                        changedItems.push(item);
                                }
                        }
                        return changedItems;
                };
                
                const discardAllChanges = () => {
                        const changedItems = getItemsWithUnsavedChanges();
                        changedItems.forEach(item => {
                                cancelChanges(item);
                        });
                };
                void discardAllChanges;
                
                const selectItem = async (item) => {
                        const changedItems = getItemsWithUnsavedChanges();
                        const otherChangedItems = changedItems.filter(ci => ci.id !== item.id);
                        
                        if (otherChangedItems.length > 0) {
                                const result = await Swal.fire({
                                        title: 'Unsaved Changes',
                                        text: otherChangedItems.length === 1 
                                                ? 'You have unsaved changes. Do you want to discard them?'
                                                : `You have unsaved changes on ${otherChangedItems.length} items. Do you want to discard them?`,
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonText: 'Discard Changes',
                                        cancelButtonText: 'Stay',
                                        customClass: {
                                                popup: 'swal-windmill-popup',
                                                title: 'swal-windmill-title',
                                                htmlContainer: 'swal-windmill-text',
                                                confirmButton: 'swal-windmill-confirm-danger',
                                                cancelButton: 'swal-windmill-cancel',
                                                actions: 'swal-windmill-actions',
                                        },
                                        buttonsStyling: false,
                                });
                                
                                if (!result.isConfirmed) {
                                        return;
                                }
                                
                                otherChangedItems.forEach(ci => cancelChanges(ci));
                        }
                        
                        selectedId.value = item.id;
                        storeOriginalValues(item);
                        if (item.children && item.children.length > 0) {
                                expandedIds.value = { ...expandedIds.value, [item.id]: true };
                        }
                };

                const openCreateModal = (parentId) => {
                        newItemName.value = '';
                        createParentId.value = parentId;
                        showCreateModal.value = true;
                };

                const closeCreateModal = () => {
                        showCreateModal.value = false;
                        newItemName.value = '';
                        createParentId.value = null;
                };

                const createItem = async () => {
                        if (!newItemName.value.trim() || isCreating.value) return;
                        isCreating.value = true;
                        try {
                                const payload = { name: newItemName.value.trim() };
                                if (createParentId.value) {
                                        payload.parentid = createParentId.value;
                                }
                                await ProductCategoryService.create(payload);
                                showToast('Category created successfully');
                                closeCreateModal();
                                await loadData();
                                if (createParentId.value) {
                                        expandedIds.value = { ...expandedIds.value, [createParentId.value]: true };
                                }
                        } catch (error) {
                                showToast(error.message || 'Failed to create item', 'error');
                        } finally {
                                isCreating.value = false;
                        }
                };

                                const confirmDelete = (item) => {
                        const hasChildren = item.children && item.children.length > 0;
                        Swal.fire({
                                title: 'Are you sure?',
                                text: hasChildren
                                        ? `This category has ${item.children.length} children. Deleting it will also delete all children!`
                                        : 'You will not be able to recover this item!',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Yes, delete it!',
                                cancelButtonText: 'Cancel',
                                customClass: {
                                        popup: 'swal-windmill-popup',
                                        title: 'swal-windmill-title',
                                        htmlContainer: 'swal-windmill-text',
                                        confirmButton: 'swal-windmill-confirm-danger',
                                        cancelButton: 'swal-windmill-cancel',
                                        actions: 'swal-windmill-actions',
                                },
                                buttonsStyling: false,
                        }).then(async (result) => {
                                if (result.isConfirmed) {
                                        try {
                                                await ProductCategoryService.delete(item.id);
                                                await loadData();
                                                if (selectedId.value === item.id) {
                                                        selectedId.value = null;
                                                }
                                                showToast('Item deleted successfully');
                                        } catch (error) {
                                                showToast(error.message || 'Failed to delete item', 'error');
                                        }
                                }
                        });
                };

                const openImagePicker = (item) => {
                        if (!filestackClient) {
                                showToast('File upload is not available', 'error');
                                return;
                        }
                        storeOriginalValues(item);
                        
                        const options = {
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: (files) => {
                                        if (files.filesUploaded && files.filesUploaded.length > 0) {
                                                const newUrl = files.filesUploaded[0].url;
                                                pendingImages.value[item.id] = newUrl;
                                                showToast('Image selected. Click Save to apply changes.');
                                        }
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const isDescendantOf = (parent, childId) => {
                        if (!parent.children) return false;
                        for (const c of parent.children) {
                                if (c.id === childId) return true;
                                if (isDescendantOf(c, childId)) return true;
                        }
                        return false;
                };

                const getSiblings = (parentId) => {
                        if (!parentId) {
                                return treeData.value;
                        }
                        const findParent = (nodes) => {
                                for (const node of nodes) {
                                        if (node.id === parentId) return node;
                                        if (node.children) {
                                                const found = findParent(node.children);
                                                if (found) return found;
                                        }
                                }
                                return null;
                        };
                        const parent = findParent(treeData.value);
                        return parent?.children || [];
                };

                const onTreeDragStart = (item) => {
                        draggedItem.value = item;
                };

                const onTreeDragOver = (item, position) => {
                        if (draggedItem.value && draggedItem.value.id !== item.id) {
                                if (!isDescendantOf(draggedItem.value, item.id)) {
                                        dragOverId.value = item.id;
                                        dropPosition.value = position;
                                }
                        }
                };

                const onTreeDragLeave = (e) => {
                        const relatedTarget = e?.relatedTarget;
                        if (relatedTarget && e?.currentTarget?.contains(relatedTarget)) {
                                return;
                        }
                        dragOverId.value = null;
                        dropPosition.value = null;
                };

                const onTreeDrop = async (targetItem) => {
                        const sourceItem = draggedItem.value;
                        const position = dropPosition.value;

                        if (!sourceItem || sourceItem.id === targetItem.id) {
                                onTreeDragEnd();
                                return;
                        }

                        if (isDescendantOf(sourceItem, targetItem.id)) {
                                showToast('Cannot move a category into its own descendant', 'error');
                                onTreeDragEnd();
                                return;
                        }

                        if (!position) {
                                onTreeDragEnd();
                                return;
                        }

                        onTreeDragEnd();

                        try {
                                if (position === 'inside') {
                                        await ProductCategoryService.update(sourceItem.id, { parentid: targetItem.id });
                                        showToast('Category moved successfully');
                                        expandedIds.value = { ...expandedIds.value, [targetItem.id]: true };
                                } else {
                                        const newParentId = targetItem.parentid || null;
                                        const siblings = getSiblings(newParentId);
                                        const targetIndex = siblings.findIndex(s => s.id === targetItem.id);
                                        const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;

                                        const filteredSiblings = siblings.filter(s => s.id !== sourceItem.id);
                                        filteredSiblings.splice(insertIndex > filteredSiblings.length ? filteredSiblings.length : insertIndex, 0, { ...sourceItem });

                                        const updates = filteredSiblings.map((item, index) => ({
                                                id: item.id,
                                                serialnumber: index,
                                                parentid: newParentId,
                                        }));

                                        await ProductCategoryService.batchUpdate(updates);
                                        showToast('Category moved successfully');
                                }
                                await loadData();
                        } catch (error) {
                                showToast(error.message || 'Failed to move category', 'error');
                        }
                };

                const onTreeDragEnd = () => {
                        draggedItem.value = null;
                        dragOverId.value = null;
                        dropPosition.value = null;
                };

                watch(loggedIn, async (isLoggedIn) => {
                        if (isLoggedIn) {
                                loadData();
                        }
                });
                
                watch(displayedItems, (items) => {
                        items.forEach(item => storeOriginalValues(item));
                }, { immediate: true });

                onMounted(() => {
                        if (auth0.isAuthenticated.value) {
                                loadData();
                        }
                });

                return {
                        allItems,
                        languages,
                        selectedLanguage,
                        selectedCardLanguage,
                        getItemLanguages,
                        getActiveLanguage,
                        pdps,
                        isLoading,
                        searchQuery,
                        treeData,
                        filteredTreeData,
                        expandedIds,
                        selectedId,
                        selectedItem,
                        displayedItems,
                        toastMessage,
                        toastVariant,
                        hasFilestackClient,
                        showCreateModal,
                        newItemName,
                        createParentId,
                        isCreating,
                        dragOverId,
                        dropPosition,
                        pendingImages,
                        loadTranslations,
                        refreshData,
                        toggleExpand,
                        selectItem,
                        openCreateModal,
                        closeCreateModal,
                        createItem,
                        confirmDelete,
                        openImagePicker,
                        saveItem,
                        cancelChanges,
                        hasItemChanges,
                        getDisplayImage,
                        handleSaveButtonHover,
                        hideToast,
                        onTreeDragStart,
                        onTreeDragOver,
                        onTreeDragLeave,
                        onTreeDrop,
                        onTreeDragEnd,
                };
        },
});
</script>

<style>
/* Mobile responsive layout */
@media (max-width: 768px) {
        .lister-main-layout {
                flex-direction: column !important;
        }
        .lister-sidebar {
                width: 100% !important;
                min-width: 100% !important;
                max-height: 300px;
        }
        .lister-sidebar .tree-container {
                max-height: 200px !important;
        }
}

/* SweetAlert Windmill Theme */
.swal-windmill-popup {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        padding: 24px !important;
        width: 380px !important;
        max-width: 90vw !important;
}

.swal-windmill-title {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        font-size: 18px !important;
        font-weight: 600 !important;
        color: #111827 !important;
        margin-bottom: 8px !important;
}

.swal-windmill-text {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        font-size: 14px !important;
        color: #6b7280 !important;
        line-height: 1.5 !important;
}

.swal-windmill-actions {
        margin-top: 20px !important;
        gap: 12px !important;
}

.swal-windmill-confirm,
.swal-windmill-confirm-danger {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        font-size: 14px !important;
        font-weight: 500 !important;
        padding: 10px 20px !important;
        border-radius: 6px !important;
        border: none !important;
        cursor: pointer !important;
        transition: background-color 0.15s !important;
}

.swal-windmill-confirm {
        background-color: #9333ea !important;
        color: white !important;
}

.swal-windmill-confirm:hover {
        background-color: #7e22ce !important;
}

.swal-windmill-confirm-danger {
        background-color: #dc2626 !important;
        color: white !important;
}

.swal-windmill-confirm-danger:hover {
        background-color: #b91c1c !important;
}

.swal-windmill-cancel {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        font-size: 14px !important;
        font-weight: 500 !important;
        padding: 10px 20px !important;
        border-radius: 6px !important;
        background-color: #f3f4f6 !important;
        color: #374151 !important;
        border: none !important;
        cursor: pointer !important;
        transition: background-color 0.15s !important;
}

.swal-windmill-cancel:hover {
        background-color: #e5e7eb !important;
}
</style>
